/**
 * Stock Calculation Utilities
 *
 * Provides functions to calculate stock from inventory_allocation
 * instead of relying on potentially incorrect total_stock/stock fields from API
 */

/**
 * Calculate total stock from inventory allocation
 * @param inventoryAllocation - Warehouse ID to stock quantity mapping
 * @returns Sum of all warehouse stock quantities
 *
 * @example
 * ```typescript
 * const allocation = { "WH-001": 10, "WH-002": 20, "dropshipping": 5 }
 * calculateStockFromAllocation(allocation) // Returns 35
 * ```
 */
export function calculateStockFromAllocation(
  inventoryAllocation: Record<string, number> | undefined
): number {
  if (!inventoryAllocation) return 0

  return Object.values(inventoryAllocation).reduce(
    (sum, quantity) => sum + quantity,
    0
  )
}

/**
 * Calculate total stock for a product (sum of all variant allocations)
 * @param product - Product with optional variants
 * @returns Total stock across all variants or product-level stock
 *
 * @example
 * ```typescript
 * // Product with variants
 * const productWithVariants = {
 *   inventoryAllocation: { "WH-001": 100 },  // Ignored when variants exist
 *   variantCombinations: [
 *     { inventoryAllocation: { "WH-001": 10, "WH-002": 5 } },  // 15
 *     { inventoryAllocation: { "WH-001": 20 } }                // 20
 *   ]
 * }
 * calculateProductTotalStock(productWithVariants) // Returns 35
 *
 * // Product without variants
 * const singleVariant = {
 *   inventoryAllocation: { "WH-001": 50, "WH-002": 30 }
 * }
 * calculateProductTotalStock(singleVariant) // Returns 80
 * ```
 */
export function calculateProductTotalStock(product: {
  inventoryAllocation: Record<string, number>
  variantCombinations?: Array<{
    inventoryAllocation: Record<string, number>
  }>
}): number {
  // For products with variants: sum all variant allocations
  if (product.variantCombinations && product.variantCombinations.length > 0) {
    return product.variantCombinations.reduce(
      (total, variant) =>
        total + calculateStockFromAllocation(variant.inventoryAllocation),
      0
    )
  }

  // For single-variant products: use product-level allocation
  return calculateStockFromAllocation(product.inventoryAllocation)
}
